/**
 * Página de Horas Sindicales y Asambleas para usuarios
 * Muestra saldo acumulado, barras de progreso, historial de movimientos
 * Estilo visual idéntico a AsuntosPropios (gradiente azul-violeta)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { horasSindicales as hsApi } from '../services/api';
import { useToast } from '../context/ToastContext';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import {
    ArrowLeft,
    Clock,
    ChevronLeft,
    ChevronRight,
    History,
    Info,
    HelpCircle,
    TrendingUp,
    TrendingDown,
    Users,
    Briefcase,
    CheckCircle
} from 'lucide-react';

export default function HorasSindicales() {
    const navigate = useNavigate();
    const toast = useToast();

    const [loading, setLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(() => {
        const saved = sessionStorage.getItem('hs_view_year');
        return saved !== null ? parseInt(saved) : new Date().getFullYear();
    });
    const [saldo, setSaldo] = useState(null);
    const [historial, setHistorial] = useState([]);
    const [activeTab, setActiveTab] = useState('historial');

    useEffect(() => {
        loadData();
    }, [currentYear]);

    useEffect(() => {
        sessionStorage.setItem('hs_view_year', currentYear.toString());
    }, [currentYear]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [saldoData, historialData] = await Promise.all([
                hsApi.getSaldo(currentYear),
                hsApi.getHistorial(currentYear)
            ]);
            setSaldo(saldoData);
            setHistorial(historialData);
        } catch (error) {
            console.error('Error loading horas sindicales data:', error);
            toast.error('Error al cargar datos de horas sindicales');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="w-8 h-8 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    const horasReunion = saldo?.horas_reunion || 0;
    const horasAsamblea = saldo?.horas_asamblea || 0;
    const horasAcumuladas = saldo?.horas_acumuladas || 0;
    const horasConsumidas = saldo?.horas_consumidas || 0;
    const disponibles = saldo?.saldo || 0;
    const maxReunion = saldo?.config?.max_horas_reunion || 40;
    const maxAsamblea = saldo?.config?.max_horas_asamblea || 16;
    const horasPorJornada = saldo?.horas_por_jornada || 8;
    const puedePedirDia = saldo?.puede_pedir_dia || false;

    const pctReunion = maxReunion > 0 ? Math.min((horasReunion / maxReunion) * 100, 100) : 0;
    const pctAsamblea = maxAsamblea > 0 ? Math.min((horasAsamblea / maxAsamblea) * 100, 100) : 0;

    return (
        <div className="space-y-0 pb-24">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-4 py-4 sm:py-6 rounded-b-3xl shadow-lg relative mb-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={() => navigate('/user')}
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div className="flex-1">
                        <h1 className="text-xl sm:text-2xl font-black text-white leading-tight">Horas Sindicales</h1>
                        <p className="text-xs sm:text-sm text-blue-100 font-medium">Reuniones y Asambleas</p>
                    </div>
                    <button
                        className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        title="Ver ayuda"
                        onClick={() => setActiveTab('info')}
                    >
                        <HelpCircle className="w-5 h-5" />
                    </button>
                    <div className="flex items-center gap-1">
                        <button
                            onClick={() => setCurrentYear(y => y - 1)}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        >
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <span className="px-2 sm:px-3 py-1 bg-white/20 rounded-lg text-sm sm:text-base font-black text-white">{currentYear}</span>
                        <button
                            onClick={() => setCurrentYear(y => y + 1)}
                            className="bg-white/20 hover:bg-white/30 backdrop-blur p-2 rounded-full transition-colors text-white"
                        >
                            <ChevronRight className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content Container */}
            <div className="px-4 sm:px-6 space-y-4">
                {/* Balance Card */}
                {saldo && (
                    <div className="bg-[var(--bg-secondary)] rounded-2xl p-4 border border-[var(--glass-border)] shadow-sm">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="font-semibold flex items-center gap-2 text-[var(--text-primary)]">
                            <Clock className="w-5 h-5 text-blue-500" />
                            Balance {currentYear}
                        </h2>
                    </div>

                    {/* Three main cards */}
                    <div className="grid grid-cols-3 gap-1.5 sm:gap-3 text-center mb-4">
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className="text-lg sm:text-2xl font-black text-blue-500 truncate">{horasAcumuladas}h</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Acumuladas</p>
                        </div>
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className="text-lg sm:text-2xl font-black text-violet-500 truncate">{horasConsumidas}h</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Usadas</p>
                        </div>
                        <div className="bg-[var(--bg-primary)] rounded-xl p-2 sm:p-3 shadow-sm border border-[var(--glass-border)] flex flex-col justify-center min-w-0">
                            <p className={`text-lg sm:text-2xl font-black ${puedePedirDia ? 'text-emerald-500' : 'text-amber-500'} truncate`}>{disponibles}h</p>
                            <p className="text-[9px] sm:text-[10px] md:text-xs font-black text-[var(--text-muted)] mt-1 uppercase tracking-tighter truncate">Disponibles</p>
                        </div>
                    </div>

                    {/* Progress bars */}
                    <div className="space-y-3 px-2">
                        {/* Reuniones */}
                        <div>
                            <div className="flex justify-between items-center text-sm mb-1">
                                <span className="flex items-center gap-1.5 font-medium text-[var(--text-primary)]">
                                    <Briefcase className="w-3.5 h-3.5 text-blue-500" />
                                    Reuniones
                                </span>
                                <span className="font-black text-blue-500">{horasReunion}h / {maxReunion}h</span>
                            </div>
                            <div className="w-full h-2.5 bg-[var(--glass-bg)] rounded-full overflow-hidden border border-[var(--glass-border)]">
                                <div
                                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all duration-500"
                                    style={{ width: `${pctReunion}%` }}
                                />
                            </div>
                            <p className="text-xs text-[var(--text-muted)] mt-0.5">
                                {saldo.count_reunion} reuniones × {saldo.config?.horas_por_reunion || 0}h
                            </p>
                        </div>

                        {/* Asambleas */}
                        <div>
                            <div className="flex justify-between items-center text-sm mb-1">
                                <span className="flex items-center gap-1.5 font-medium text-[var(--text-primary)]">
                                    <Users className="w-3.5 h-3.5 text-violet-500" />
                                    Asambleas
                                </span>
                                <span className="font-black text-violet-500">{horasAsamblea}h / {maxAsamblea}h</span>
                            </div>
                            <div className="w-full h-2.5 bg-[var(--glass-bg)] rounded-full overflow-hidden border border-[var(--glass-border)]">
                                <div
                                    className="h-full bg-gradient-to-r from-violet-500 to-purple-400 rounded-full transition-all duration-500"
                                    style={{ width: `${pctAsamblea}%` }}
                                />
                            </div>
                            <p className="text-xs text-[var(--text-muted)] mt-0.5">
                                {saldo.count_asamblea} asambleas × {saldo.config?.horas_por_asamblea || 0}h
                            </p>
                        </div>
                    </div>

                    {/* Day request status */}
                    <div className="mt-4 pt-3 border-t border-[var(--glass-border)] px-2">
                        <div className="flex items-center gap-2 text-sm">
                            <span className="text-[var(--text-muted)] font-medium">Para 1 día libre:</span>
                            <span className="font-bold text-[var(--text-primary)]">{horasPorJornada}h necesarias</span>
                        </div>
                        {puedePedirDia ? (
                            <div className="flex items-center gap-2 mt-1.5 text-emerald-500">
                                <CheckCircle className="w-4 h-4" />
                                <span className="text-xs sm:text-sm font-bold">Puedes pedir un día libre</span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 mt-1.5 text-amber-500">
                                <Clock className="w-4 h-4" />
                                <span className="text-xs sm:text-sm font-medium">
                                    Te faltan {Math.max(0, horasPorJornada - disponibles)}h para un día libre
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Tabs */}
                <div className="flex gap-2 py-1">
                    <button
                        onClick={() => setActiveTab('historial')}
                        className={`flex-1 flex justify-center items-center gap-2 px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${activeTab === 'historial'
                            ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-md shadow-blue-500/20'
                            : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                            }`}
                    >
                        <History className="w-4 h-4" />
                        Historial
                    </button>
                    <button
                        onClick={() => setActiveTab('info')}
                        className={`flex-1 flex justify-center items-center gap-2 px-2 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-bold transition-all ${activeTab === 'info'
                            ? 'bg-gradient-to-r from-blue-500 to-violet-500 text-white shadow-md shadow-blue-500/20'
                            : 'bg-[var(--glass-bg)] text-[var(--text-muted)] border border-[var(--glass-border)] hover:bg-[var(--glass-border)]'
                            }`}
                    >
                        <Info className="w-4 h-4" />
                        Info
                    </button>
                </div>

            {/* Historial Tab */}
            {activeTab === 'historial' && (
                <div className="space-y-3">
                    {historial.length === 0 ? (
                        <div className="text-center py-8 text-[var(--text-muted)]">
                            <History className="w-12 h-12 mx-auto mb-2 opacity-50" />
                            <p>Sin movimientos para {currentYear}</p>
                            <p className="text-xs mt-1">Marca días con motivo "Reunión Sindical" o "Asamblea" desde tu calendario</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {historial.map(mov => {
                                const isConsumo = mov.tipo_hora_sindical === 'consumo_horas_sindicales';
                                const isReunion = mov.tipo_hora_sindical === 'reunion_sindical';
                                const isAsamblea = mov.tipo_hora_sindical === 'asamblea';

                                return (
                                    <div key={mov.id} className="flex gap-3 p-3 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${isConsumo ? 'bg-emerald-500/20' : isReunion ? 'bg-blue-500/20' : 'bg-violet-500/20'
                                            }`}>
                                            {isConsumo ? (
                                                <TrendingDown className="w-4 h-4 text-emerald-500" />
                                            ) : (
                                                <TrendingUp className={`w-4 h-4 ${isReunion ? 'text-blue-500' : 'text-violet-500'}`} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <p className="font-bold text-sm text-[var(--text-primary)]">
                                                    {mov.motivo_nombre}
                                                </p>
                                                <span className={`text-sm font-black ${mov.horas > 0 ? 'text-blue-500' : 'text-emerald-500'}`}>
                                                    {mov.horas > 0 ? '+' : ''}{mov.horas}h
                                                </span>
                                            </div>
                                            <p className="text-xs font-medium text-[var(--text-muted)] mt-0.5">
                                                {format(parseISO(mov.fecha), 'EEEE d MMMM yyyy', { locale: es })}
                                            </p>
                                            {mov.comentario && (
                                                <p className="text-xs text-[var(--text-muted)] mt-1 italic opacity-80">"{mov.comentario}"</p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {/* Info Tab */}
            {activeTab === 'info' && (
                <div className="space-y-4">
                    <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4">
                        <h3 className="font-bold text-[var(--text-primary)] mb-3 flex items-center gap-2">
                            <Info className="w-4 h-4 text-blue-500" />
                            ¿Cómo funciona?
                        </h3>
                        <div className="space-y-3 text-sm text-[var(--text-muted)]">
                            <div className="flex gap-3 items-start">
                                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-blue-400">1</span>
                                </div>
                                <p>Cuando asistes a una <strong className="text-blue-400">Reunión Sindical</strong> o una <strong className="text-violet-400">Asamblea</strong>, márcalo en tu calendario.</p>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-blue-400">2</span>
                                </div>
                                <p>Cada asistencia te suma <strong className="text-[var(--text-primary)]">{saldo?.config?.horas_por_reunion || 2} horas</strong> que se acumulan en tu bolsa.</p>
                            </div>
                            <div className="flex gap-3 items-start">
                                <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <span className="text-xs font-bold text-blue-400">3</span>
                                </div>
                                <p>Cuando acumules <strong className="text-emerald-400">{horasPorJornada} horas</strong>, podrás marcar un <strong className="text-emerald-400">"Día por Horas Sindicales"</strong> para obtener un día libre.</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-xl p-4">
                        <h3 className="font-bold text-[var(--text-primary)] mb-3">📊 Configuración actual</h3>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="bg-[var(--bg-primary)] rounded-lg p-2.5 border border-[var(--glass-border)]">
                                <p className="text-[var(--text-muted)] text-xs">Horas por reunión</p>
                                <p className="font-bold text-blue-400">{saldo?.config?.horas_por_reunion || 0}h</p>
                            </div>
                            <div className="bg-[var(--bg-primary)] rounded-lg p-2.5 border border-[var(--glass-border)]">
                                <p className="text-[var(--text-muted)] text-xs">Máx. reuniones</p>
                                <p className="font-bold text-blue-400">{maxReunion}h</p>
                            </div>
                            <div className="bg-[var(--bg-primary)] rounded-lg p-2.5 border border-[var(--glass-border)]">
                                <p className="text-[var(--text-muted)] text-xs">Horas por asamblea</p>
                                <p className="font-bold text-violet-400">{saldo?.config?.horas_por_asamblea || 0}h</p>
                            </div>
                            <div className="bg-[var(--bg-primary)] rounded-lg p-2.5 border border-[var(--glass-border)]">
                                <p className="text-[var(--text-muted)] text-xs">Máx. asambleas</p>
                                <p className="font-bold text-violet-400">{maxAsamblea}h</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4">
                        <p className="text-sm text-amber-400 font-medium">
                            ⚠️ Las horas se acumulan cuando marcas los motivos "Reunión Sindical" o "Asamblea" desde tu calendario.
                            Para pedir un día libre, marca el motivo "Día por Horas Sindicales" en el día que quieras librar.
                        </p>
                    </div>
                </div>
            )}
            </div>
        </div>
    );
}
